import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Lock,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Card from "../../components/ui/Card";
import { useForm } from "react-hook-form";
import { SECURITY_CONFIG } from "../../config/constants";
import { useAuth } from "../../contexts/AuthContext";

const ResetPassword = () => {
  const { loadingAuthActions, resetPassword } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Token comes from the reset link emailed to the user
  const token = searchParams.get("token");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm();
  const watchPassword = watch("password");

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < SECURITY_CONFIG.passwordMinLength) {
      errors.push(`At least ${SECURITY_CONFIG.passwordMinLength} characters`);
    }

    if (SECURITY_CONFIG.passwordRequireUppercase && !/[A-Z]/.test(password)) {
      errors.push("One uppercase letter");
    }

    if (SECURITY_CONFIG.passwordRequireLowercase && !/[a-z]/.test(password)) {
      errors.push("One lowercase letter");
    }

    if (SECURITY_CONFIG.passwordRequireNumbers && !/\d/.test(password)) {
      errors.push("One number");
    }

    if (
      SECURITY_CONFIG.passwordRequireSpecialChars &&
      !/[!@#$%^&*(),.?":{}|<>]/.test(password)
    ) {
      errors.push("One special character");
    }

    return errors.length === 0
      ? true
      : `Password must contain: ${errors.join(", ")}`;
  };

  const onSubmit = async (data) => {
    const response = await resetPassword(token, data.password);

    if (response.success) {
      reset();
      setIsSuccess(true);
    }
  };

  // Link opened without a token (or a malformed one)
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card className="p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
                Invalid reset link
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                This password reset link is missing its token. Request a new
                link and open it directly from your email.
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <Button
                onClick={() => navigate("/auth/forgot-password")}
                className="w-full"
              >
                Request a new link
              </Button>

              <div className="text-center">
                <Link
                  to="/auth/login"
                  className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to login
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card className="p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
                Password reset successful
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Your password has been updated successfully.
              </p>
            </div>

            <div className="mt-8">
              <Button
                onClick={() => navigate("/auth/login")}
                className="w-full"
              >
                Continue to login
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Choose a new password for your account.
          </p>
        </div>

        <Card className="p-8">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <Input
              label="New Password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              {...register("password", {
                required: "Password is required",
                validate: validatePassword,
              })}
              error={errors.password?.message}
              leftIcon={<Lock className="w-4 h-4 text-gray-400" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            <Input
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === watchPassword || "Passwords do not match",
              })}
              error={errors.confirmPassword?.message}
              leftIcon={<Lock className="w-4 h-4 text-gray-400" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              }
            />

            {/* Password Requirements */}
            {watchPassword && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p className="font-medium mb-2">Password requirements:</p>
                <ul className="space-y-1">
                  <li
                    className={`flex items-center ${
                      watchPassword.length >= SECURITY_CONFIG.passwordMinLength
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  >
                    <CheckCircle className="w-3 h-3 mr-2" />
                    At least {SECURITY_CONFIG.passwordMinLength} characters
                  </li>
                  {SECURITY_CONFIG.passwordRequireUppercase && (
                    <li
                      className={`flex items-center ${
                        /[A-Z]/.test(watchPassword)
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    >
                      <CheckCircle className="w-3 h-3 mr-2" />
                      One uppercase letter
                    </li>
                  )}
                  {SECURITY_CONFIG.passwordRequireLowercase && (
                    <li
                      className={`flex items-center ${
                        /[a-z]/.test(watchPassword)
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    >
                      <CheckCircle className="w-3 h-3 mr-2" />
                      One lowercase letter
                    </li>
                  )}
                  {SECURITY_CONFIG.passwordRequireNumbers && (
                    <li
                      className={`flex items-center ${
                        /\d/.test(watchPassword)
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    >
                      <CheckCircle className="w-3 h-3 mr-2" />
                      One number
                    </li>
                  )}
                  {SECURITY_CONFIG.passwordRequireSpecialChars && (
                    <li
                      className={`flex items-center text-sm ${
                        /[!@#$%^&*(),.?":{}|<>]/.test(watchPassword)
                          ? "text-green-600"
                          : "text-gray-400"
                      }`}
                    >
                      <CheckCircle className="w-3 h-3 mr-2" />
                      One special character
                    </li>
                  )}
                </ul>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              loading={loadingAuthActions}
              disabled={loadingAuthActions}
            >
              {loadingAuthActions ? "Updating..." : "Update password"}
            </Button>

            <div className="text-center">
              <Link
                to="/auth/login"
                className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to login
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
